import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from '@aimsos/i18n';
import { Button, ModalLayout, Textfield } from '@aimsos/ui';

import { useWorkspaceSaveFlow } from '../hooks';
import WorkspaceLogoDropzone from './WorkspaceLogoDropzone';

const MAX_LOGO_BYTES = 2 * 1024 * 1024;
const LOGO_ACCEPT = 'image/png,image/jpeg,image/jpg,image/svg+xml';

/**
 * Edit workspace — POST /v1/workspace/update uses the same body shape as create, plus `workspace_uuid`.
 *
 * @param {object} props
 * @param {boolean} props.isOpen
 * @param {() => void} props.onClose
 * @param {string} props.workspaceId
 * @param {string} [props.customerSlug] — tenant slug for POST /v1/file/upload-url `customer` when replacing logo
 * @param {string} props.initialName
 * @param {string} [props.initialContactEmail] — `contact_email` (owner email from workspace detail)
 * @param {string} [props.initialContactName] — `contact_name` (owner name from workspace detail)
 * @param {string} [props.initialAddress]
 * @param {string} [props.initialPhone] — `phone_number` (same as create; default if empty)
 * @param {string | null} [props.initialPhotoUrl]
 * @param {() => void} [props.onUpdated] — after successful save (e.g. refetch overview)
 */
const EditWorkspaceModal = ({
  isOpen,
  onClose,
  workspaceId,
  customerSlug = '',
  initialName = '',
  initialContactEmail = '',
  initialContactName = '',
  initialAddress = '',
  initialPhone = '',
  initialPhotoUrl = null,
  onUpdated,
}) => {
  const { t } = useTranslation('workspaces');
  const { executeUpdate: runUpdateWorkspaceApi } = useWorkspaceSaveFlow(t);
  const titleId = useId();
  const fileInputRef = useRef(null);

  const [name, setName] = useState('');
  const [address, setAddress] = useState('');
  const [logoFile, setLogoFile] = useState(null);
  const [logoError, setLogoError] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [dragActive, setDragActive] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setName(initialName ?? '');
    setAddress(initialAddress ?? '');
    setLogoFile(null);
    setLogoError('');
    setSubmitted(false);
    setSubmitting(false);
    setSubmitError(null);
    setDragActive(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, [isOpen, initialName, initialContactEmail, initialContactName, initialAddress, initialPhone, workspaceId]);

  const validateFile = useCallback(
    (file) => {
      if (!file) {
        setLogoFile(null);
        setLogoError('');
        return;
      }
      const type = (file.type || '').toLowerCase();
      const okType =
        type === 'image/png' ||
        type === 'image/jpeg' ||
        type === 'image/jpg' ||
        type === 'image/svg+xml';
      if (!okType) {
        setLogoError(t('list.createModal.logoType'));
        return;
      }
      if (file.size > MAX_LOGO_BYTES) {
        setLogoError(t('list.createModal.logoTooLarge'));
        return;
      }
      setLogoError('');
      setLogoFile(file);
    },
    [t],
  );

  const nameError = submitted && !name.trim() ? t('detail.overview.editModal.nameRequired') : '';

  const handleDismiss = useCallback(() => {
    if (submitting) return;
    onClose?.();
  }, [onClose, submitting]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') handleDismiss();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isOpen, handleDismiss]);

  const handleSubmit = useCallback(async () => {
    setSubmitted(true);
    setSubmitError(null);
    if (!name.trim() || !workspaceId) return;
    if (logoError) return;

    setSubmitting(true);
    try {
      const result = await runUpdateWorkspaceApi({
        workspaceId,
        customerSlug,
        trimmedName: name.trim(),
        contactEmail: initialContactEmail,
        contactName: initialContactName,
        address: address.trim(),
        phoneNumber: initialPhone,
        logoFile,
      });

      if (result.type === 'photo_error') {
        onUpdated?.();
        setSubmitError(result.message);
        return;
      }

      onUpdated?.();
      onClose?.();
    } catch (err) {
      setSubmitError(err?.message ?? t('detail.overview.editModal.submitError'));
    } finally {
      setSubmitting(false);
    }
  }, [
    address,
    customerSlug,
    initialContactEmail,
    initialContactName,
    initialPhone,
    logoError,
    logoFile,
    name,
    onClose,
    onUpdated,
    runUpdateWorkspaceApi,
    t,
    workspaceId,
  ]);

  const onFileInputChange = useCallback(
    (e) => {
      const file = e.target.files?.[0];
      validateFile(file ?? null);
    },
    [validateFile],
  );

  const onDrop = useCallback(
    (e) => {
      e.preventDefault();
      setDragActive(false);
      const file = e.dataTransfer.files?.[0];
      validateFile(file ?? null);
    },
    [validateFile],
  );

  return (
    <ModalLayout
      isOpen={isOpen}
      onClose={handleDismiss}
      title={t('detail.overview.editModal.title')}
      subtitle={t('detail.overview.editModal.subtitle')}
      titleId={titleId}
      closeAriaLabel={t('detail.overview.editModal.closeAria')}
      maxWidthClassName="max-w-xl"
    >
      <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
        <div className="flex flex-col gap-4">
        <Textfield
          label={t('detail.overview.editModal.nameLabel')}
          placeholder={t('detail.overview.editModal.namePlaceholder')}
          value={name}
          onChange={setName}
          state={nameError ? 'error' : 'default'}
          supportingText={nameError || undefined}
        />

        <Textfield
          label={t('detail.overview.editModal.addressLabel')}
          placeholder={t('detail.overview.editModal.addressPlaceholder')}
          value={address}
          onChange={setAddress}
        />

        <div className="flex flex-col gap-1.5">
          <p className="text-[14px] font-bold leading-tight text-content">{t('list.createModal.logoLabel')}</p>
          <WorkspaceLogoDropzone
            inputId="edit-workspace-logo"
            fileInputRef={fileInputRef}
            accept={LOGO_ACCEPT}
            logoFile={logoFile}
            existingImageUrl={initialPhotoUrl}
            workspaceName={name}
            dragActive={dragActive}
            onDragEnter={(e) => {
              e.preventDefault();
              setDragActive(true);
            }}
            onDragLeave={(e) => {
              e.preventDefault();
              if (!e.currentTarget.contains(e.relatedTarget)) setDragActive(false);
            }}
            onDrop={onDrop}
            onFileInputChange={onFileInputChange}
            hasError={Boolean(logoError)}
            hintBold={t('list.createModal.logoHintBold')}
            hintFormats={t('list.createModal.logoHintFormats')}
            altPreview={t('list.createModal.logoPreviewAlt')}
          />
          {logoError ? <p className="px-1 text-xs text-red-700 dark:text-red-300">{logoError}</p> : null}
        </div>

        {submitError ? (
          <p className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800 dark:border-red-900/50 dark:bg-red-950/40 dark:text-red-200">
            {submitError}
          </p>
        ) : null}
        </div>
      </div>

      <div className="shrink-0 bg-surface-muted/20 px-5 py-4">
        <div className="flex justify-end gap-2">
          <Button variant="secondary" type="button" size="md" onClick={handleDismiss} disabled={submitting}>
            {t('detail.overview.editModal.cancel')}
          </Button>
          <Button variant="primary" type="button" size="md" loading={submitting} onClick={handleSubmit}>
            {submitting ? t('detail.overview.editModal.updating') : t('detail.overview.editModal.update')}
          </Button>
        </div>
      </div>
    </ModalLayout>
  );
};

export default EditWorkspaceModal;
